import React, { useState } from 'react';
import { useQuery, useMutation } from 'react-query';
import { receiptAPI } from '../../services/api';
import SavedPaymentMethodsPanel from '../../components/payments/SavedPaymentMethodsPanel';
import LoadingSpinner from '../../components/LoadingSpinner';
import PageLayout from '../../components/PageLayout';
import toast from 'react-hot-toast';
import {
  DocumentTextIcon,
  CreditCardIcon,
  EyeIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  CalendarIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';

function BillingPage() {
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    status: '',
    page: 1,
    limit: 20
  });
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);

  const { data: invoicesData, isLoading: invoicesLoading } = useQuery(
    ['customer-invoices', filters],
    () => receiptAPI.getReceipts(filters),
    { 
      select: (response) => response.data,
      keepPreviousData: true
    }
  );


  const { data: billingStats } = useQuery(
    'billing-stats',
    () => receiptAPI.getReceipts({ limit: 1000 }),
    {
      select: (response) => {
        const receipts = response.data || [];
        const total = receipts.reduce((sum: number, receipt: any) => sum + parseFloat(receipt.amount || '0'), 0);
        const paid = receipts.filter((r: any) => r.status === 'paid');
        const pending = receipts.filter((r: any) => r.status === 'pending');
        const overdue = receipts.filter((r: any) => r.status === 'overdue');

        return {
          totalSpent: total,
          totalInvoices: receipts.length,
          paidInvoices: paid.length,
          pendingInvoices: pending.length,
          overdueInvoices: overdue.length,
          lastPayment: receipts[0]?.date || null
        };
      }
    }
  );

  const resendEmailMutation = useMutation(
    receiptAPI.resendInvoiceEmail,
    {
      onSuccess: () => {
        toast.success('Invoice email sent successfully');
      },
      onError: () => {
        toast.error('Failed to send invoice email');
      }
    }
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'bg-success-container text-success-on-container';
      case 'pending': return 'bg-warning-container text-warning-on-container';
      case 'overdue': return 'bg-error-container text-error-on-container';
      default: return 'bg-surface-container-low text-on-surface-variant';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid': return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'pending': return <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />;
      case 'overdue': return <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />;
      default: return <DocumentTextIcon className="w-5 h-5 text-on-surface-variant" />;
    }
  };

  const formatCurrency = (amount: string | number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(typeof amount === 'string' ? parseFloat(amount) : amount);
  };

  const invoices = invoicesData || [];

  return (
    <PageLayout>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-on-surface">Billing & Invoices</h1>
        <p className="mt-2 text-on-surface-variant">Manage your payments, invoices, and billing information</p>
      </div>

      {/* Billing Overview */}
      {billingStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="card p-6">
            <div className="flex items-center">
              <CreditCardIcon className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-on-surface-variant">Total Spent</p>
                <p className="text-2xl font-bold text-on-surface">
                  {formatCurrency(billingStats.totalSpent)}
                </p>
              </div>
            </div>
          </div>

          <div className="card p-6">
            <div className="flex items-center">
              <DocumentTextIcon className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-on-surface-variant">Total Invoices</p>
                <p className="text-2xl font-bold text-on-surface">{billingStats.totalInvoices}</p>
                <p className="text-sm text-green-600">{billingStats.paidInvoices} paid</p>
              </div>
            </div>
          </div>

          <div className="card p-6">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-8 w-8 text-yellow-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-on-surface-variant">Pending</p>
                <p className="text-2xl font-bold text-on-surface">{billingStats.pendingInvoices}</p>
                {billingStats.overdueInvoices > 0 && (
                  <p className="text-sm text-red-600">{billingStats.overdueInvoices} overdue</p>
                )}
              </div>
            </div>
          </div>

          <div className="card p-6">
            <div className="flex items-center">
              <CalendarIcon className="h-8 w-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-on-surface-variant">Last Payment</p>
                <p className="text-lg font-bold text-on-surface">
                  {billingStats.lastPayment 
                    ? new Date(billingStats.lastPayment).toLocaleDateString()
                    : 'None'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mb-8"><SavedPaymentMethodsPanel /></div>

      {/* Filters */}
      <div className="card p-6 mb-6">
        <div className="flex items-center space-x-2 mb-4">
          <FunnelIcon className="h-5 w-5 text-on-surface-variant" />
          <h3 className="text-lg font-medium text-on-surface">Filter Invoices</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-on-surface-variant mb-1">Start Date</label>
            <input
              type="date"
              className="input-field"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value, page: 1 })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-on-surface-variant mb-1">End Date</label>
            <input
              type="date"
              className="input-field"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value, page: 1 })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-on-surface-variant mb-1">Status</label>
            <select
              className="input-field"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
            >
              <option value="">All Statuses</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="overdue">Overdue</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setFilters({ startDate: '', endDate: '', status: '', page: 1, limit: 20 })}
              className="btn-secondary w-full"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Invoices List */}
      <div className="card">
        <div className="p-6">
          <h3 className="text-lg font-medium text-on-surface">Invoice History</h3>
        </div>

        {invoicesLoading ? (
          <LoadingSpinner className="py-20" />
        ) : invoices.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-surface-container-low">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-on-surface-variant uppercase tracking-wider">
                    Invoice
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-on-surface-variant uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-on-surface-variant uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-on-surface-variant uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-on-surface-variant uppercase tracking-wider">
                    Due Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-on-surface-variant uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-surface-container-lowest">
                {invoices.map((invoice: any) => (
                  <tr key={invoice.id} className="hover:bg-surface-container-low">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(invoice.status)}
                        <div className="ml-3">
                          <div className="text-sm font-medium text-on-surface">
                            #{invoice.invoiceNumber || invoice.id.substring(0, 8)}
                          </div>
                          <div className="text-sm text-on-surface-variant">
                            {invoice.description || 'Vehicle Reservation'}
                          </div>
                        </div>
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-on-surface">
                      {new Date(invoice.date || invoice.createdAt).toLocaleDateString()}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-on-surface">
                      {formatCurrency(invoice.amount)}
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-pill ${getStatusColor(invoice.status)}`}>
                        {invoice.status}
                      </span>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-on-surface">
                      {invoice.dueDate 
                        ? new Date(invoice.dueDate).toLocaleDateString()
                        : '-'
                      }
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setSelectedInvoice(invoice)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Details"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        
                        {invoice.status !== 'paid' && (
                          <button
                            onClick={() => resendEmailMutation.mutate(invoice.id)}
                            disabled={resendEmailMutation.isLoading}
                            className="text-purple-600 hover:text-purple-900"
                            title="Resend Email"
                          >
                            📧
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <DocumentTextIcon className="h-12 w-12 text-on-surface-variant mx-auto mb-4" />
            <h3 className="text-lg font-medium text-on-surface mb-2">No invoices found</h3>
            <p className="text-on-surface-variant">No invoices match your current filters.</p>
          </div>
        )}
      </div>

      {/* Invoice Details Modal */}
      {selectedInvoice && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 w-11/12 md:w-3/4 lg:w-1/2 shadow-elevated rounded-card bg-surface-container-lowest">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-on-surface">Invoice Details</h3>
              <button
                onClick={() => setSelectedInvoice(null)}
                className="text-on-surface-variant hover:text-on-surface"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-on-surface-variant">Invoice Number</p>
                  <p className="text-lg text-on-surface">
                    #{selectedInvoice.invoiceNumber || selectedInvoice.id.substring(0, 8)}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-on-surface-variant">Status</p>
                  <span className={`px-2 py-1 text-sm rounded-pill ${getStatusColor(selectedInvoice.status)}`}>
                    {selectedInvoice.status}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-medium text-on-surface-variant">Amount</p>
                  <p className="text-lg font-bold text-on-surface">
                    {formatCurrency(selectedInvoice.amount)}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-on-surface-variant">Date Issued</p>
                  <p className="text-lg text-on-surface">
                    {new Date(selectedInvoice.date || selectedInvoice.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>

              {selectedInvoice.lineItems && (
                <div>
                  <h4 className="text-md font-medium text-on-surface mb-2">Line Items</h4>
                  <div className="rounded-card overflow-hidden bg-surface-container-low">
                    {selectedInvoice.lineItems.map((item: any, index: number) => (
                      <div key={index} className="flex justify-between items-center p-3">
                        <div>
                          <p className="font-medium text-on-surface">{item.description}</p>
                          <p className="text-sm text-on-surface-variant">Qty: {item.quantity}</p>
                        </div>
                        <p className="font-medium text-on-surface">{formatCurrency(item.amount)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedInvoice.status === 'pending' && (
                <div className="flex space-x-3 pt-4">
                  <button className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700">
                    Pay Now
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
}

export default BillingPage;
